import React, { useCallback, useEffect, useState } from 'react';
import * as reviewsApi from '../lib/supabase';
import { Star, RotateCw, MessageCircleHeart } from 'lucide-react';

const formatReviewDate = (review) => {
  const value = review.review_date || review.created_at;
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const ReviewStars = ({ rating }) => (
  <span
    className="inline-flex items-center gap-1"
    role="img"
    aria-label={`${rating} out of 5 stars`}
  >
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        aria-hidden="true"
        className={`h-[17px] w-[17px] ${
          star <= rating ? 'fill-[#e9c66a] text-[#e9c66a]' : 'fill-transparent text-white/25'
        }`}
        strokeWidth={1.7}
      />
    ))}
  </span>
);

const ReviewSkeleton = () => (
  <div className="animate-pulse border-b border-white/10 py-8 sm:py-10" aria-hidden="true">
    <div className="mb-5 flex items-center justify-between gap-4">
      <div className="h-4 w-36 rounded bg-white/10" />
      <div className="h-4 w-24 rounded bg-white/10" />
    </div>
    <div className="mb-3 h-4 w-full rounded bg-white/10" />
    <div className="mb-3 h-4 w-11/12 rounded bg-white/10" />
    <div className="h-4 w-2/3 rounded bg-white/10" />
  </div>
);

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    const loadReviews = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await reviewsApi.fetchCompanyReviews();
        if (isCurrent) {
          setReviews(Array.isArray(result) ? result : []);
        }
      } catch (loadError) {
        if (isCurrent) {
          setError('We couldn’t load the reviews just now. Please try again.');
        }
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadReviews();
    return () => {
      isCurrent = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const ratedReviews = reviews.filter((review) => Number.isFinite(Number(review.rating)));
  const averageRating = ratedReviews.length
    ? (
        ratedReviews.reduce(
          (total, review) => total + Math.min(5, Math.max(1, Number(review.rating))),
          0
        ) / ratedReviews.length
      ).toFixed(1)
    : null;

  return (
    <div className="min-h-[100dvh] overflow-hidden bg-[#100b18] text-[#f7f0df]">
      <section className="relative isolate px-5 pb-10 pt-28 sm:px-8 sm:pt-36 lg:px-10 lg:pt-40">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        >
          <div className="absolute -right-40 -top-36 h-[34rem] w-[34rem] rounded-full border border-[#c9a957]/15 sm:right-[-7rem] sm:top-[-16rem] sm:h-[52rem] sm:w-[52rem]" />
          <div className="absolute -right-24 -top-20 h-[26rem] w-[26rem] rounded-full border border-[#c9a957]/10 sm:right-[-3rem] sm:top-[-11rem] sm:h-[42rem] sm:w-[42rem]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_15%_0%,rgba(85,47,112,0.34),transparent_48%)]" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-[#100b18]" />
        </div>

        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#ddc16e]">
              <span className="h-px w-8 bg-[#ddc16e]/70" />
              The Wisdom Network
            </p>
            <h1 className="max-w-3xl font-title text-5xl font-semibold leading-[1.04] tracking-[-0.04em] text-[#fbf6e9] sm:text-6xl md:text-7xl">
              A community
              <br />
              <span className="font-serif font-normal italic text-[#ddc16e]">of good experiences.</span>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-7 text-[#d1c7d7] sm:text-lg sm:leading-8">
              Honest reflections from people who have found space for care, clarity, and connection
              in our community.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/15 pt-6">
              {!loading && !error && (
                <>
                  <div className="flex items-center gap-3">
                    <ReviewStars rating={averageRating ? Math.round(Number(averageRating)) : 0} />
                    <span className="font-mono text-sm text-[#f7f0df]">
                      {averageRating || '—'}
                      <span className="ml-1 text-[#c7bacf]">average</span>
                    </span>
                  </div>
                  <span className="h-4 w-px bg-white/20" aria-hidden="true" />
                  <p className="font-mono text-sm text-[#c7bacf]">
                    {reviews.length.toLocaleString()} {reviews.length === 1 ? 'reflection' : 'reflections'}
                  </p>
                </>
              )}
              {loading && (
                <div className="h-4 w-52 animate-pulse rounded bg-white/10" aria-label="Loading review summary" />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 pb-24 sm:px-8 lg:px-10 lg:pb-32" aria-labelledby="reviews-heading">
        <div className="mx-auto max-w-6xl">
          <div className="mb-2 flex flex-col justify-between gap-3 border-b border-[#ddc16e]/35 pb-5 sm:flex-row sm:items-end">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#a995bb]">
                Shared with care
              </p>
              <h2 id="reviews-heading" className="font-title text-2xl font-semibold text-[#fbf6e9] sm:text-3xl">
                Community reviews
              </h2>
            </div>
            {!loading && !error && reviews.length > 0 && (
              <p className="font-mono text-xs uppercase tracking-[0.12em] text-[#a995bb]">
                {reviews.length.toLocaleString()} total
              </p>
            )}
          </div>

          {loading ? (
            <div aria-label="Loading community reviews" aria-busy="true">
              {[0, 1, 2, 3].map((item) => <ReviewSkeleton key={item} />)}
            </div>
          ) : error ? (
            <div
              className="my-8 flex flex-col items-start gap-5 border border-[#8b6d91]/50 bg-[#1a1222] px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8"
              role="alert"
            >
              <div>
                <h3 className="font-title text-xl font-semibold text-[#fbf6e9]">The reviews are taking a moment.</h3>
                <p className="mt-2 max-w-xl text-sm leading-6 text-[#c7bacf]">{error}</p>
              </div>
              <button
                type="button"
                onClick={retry}
                className="inline-flex min-h-11 shrink-0 items-center gap-2 border border-[#ddc16e]/70 px-4 py-2 text-sm font-medium text-[#f2d783] transition-colors hover:bg-[#ddc16e]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ddc16e]"
              >
                <RotateCw aria-hidden="true" className="h-4 w-4" />
                Try again
              </button>
            </div>
          ) : reviews.length === 0 ? (
            <div className="my-8 border border-dashed border-[#8b6d91]/50 bg-[#17101f]/70 px-6 py-14 text-center sm:py-20">
              <MessageCircleHeart aria-hidden="true" className="mx-auto h-8 w-8 text-[#ddc16e]" strokeWidth={1.4} />
              <h3 className="mt-5 font-title text-2xl font-semibold text-[#fbf6e9]">A little space for the first story.</h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#c7bacf]">
                There aren’t any approved reviews to share yet. Please check back soon.
              </p>
            </div>
          ) : (
            <ol className="divide-y divide-white/10">
              {reviews.map((review, index) => {
                const rating = Math.min(5, Math.max(1, Math.round(Number(review.rating) || 0)));
                const date = formatReviewDate(review);
                const reviewer = review.reviewer_name?.trim() || 'Community member';
                const comment = typeof review.comment === 'string' ? review.comment : '';

                return (
                  <li key={review.id ?? `${reviewer}-${index}`}>
                    <article className="relative grid gap-5 py-8 sm:grid-cols-[minmax(9rem,0.28fr)_1fr] sm:gap-10 sm:py-10 lg:gap-16">
                      <div className="flex flex-row items-center justify-between gap-4 sm:flex-col sm:items-start sm:justify-start sm:gap-4">
                        <div>
                          <h3 className="font-title text-base font-semibold text-[#f5eddc]">
                            {reviewer}
                          </h3>
                          {date && (
                            <time
                              className="mt-1.5 block font-mono text-[11px] tracking-wide text-[#a995bb]"
                              dateTime={review.review_date || review.created_at}
                            >
                              {date}
                            </time>
                          )}
                        </div>
                        <ReviewStars rating={rating} />
                      </div>
                      <div className="relative min-w-0">
                        <span
                          aria-hidden="true"
                          className="absolute -left-1 -top-5 font-serif text-5xl leading-none text-[#ddc16e]/35 sm:-left-7 sm:-top-3"
                        >
                          “
                        </span>
                        <p className="relative whitespace-pre-line break-words text-[15px] leading-7 text-[#e0d8e4] sm:text-base sm:leading-8">
                          {comment || 'No written comment was provided.'}
                        </p>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ol>
          )}

          <p className="mt-8 max-w-3xl border-l border-[#ddc16e]/60 pl-4 text-xs leading-5 text-[#a995bb]">
            Reviews reflect the personal experiences of their authors. Each person’s path and experience is unique.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
